// CheckInFlow.jsx
// 3-step wrapper for the front-desk check-in:
//   1) Pick visit type — Cosmetic or Health
//   2) Cosmetic only: capture 5 face photos (front, L 45°, R 45°, L profile, R profile)
//   3) Hand off to ConsentSigningFlow (existing — handles consents + medical questionnaire)
//
// Photos are uploaded to Supabase Storage (bucket "checkin-photos") and the
// public URLs are passed back to the parent so the checkins row stores them
// in face_photos (JSONB).

import { useState, useRef, useEffect } from "react";
import { supabase } from "./supabase";
import { ConsentSigningFlow } from "./ConsentManager";
import { QuestionnaireRunner } from "./ConsentsManager";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)",
  purple:"#7c3aed",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const PHOTO_ANGLES = [
  { key: "front",     label: "Front",        emoji: "👤", instruction: "Face the camera straight on, neutral expression." },
  { key: "left_45",   label: "Left 45°",     emoji: "↖️", instruction: "Turn your head about 45° to the right (so we see your left side)." },
  { key: "right_45",  label: "Right 45°",    emoji: "↗️", instruction: "Turn your head about 45° to the left (so we see your right side)." },
  { key: "left_full", label: "Left Profile", emoji: "⬅️", instruction: "Full left profile — face the wall to your right." },
  { key: "right_full",label: "Right Profile",emoji: "➡️", instruction: "Full right profile — face the wall to your left." },
];

export default function CheckInFlow({ client, appointment, onComplete, onCancel }) {
  // step: "type" | "photos" | "questionnaire" | "consents"
  const [step, setStep] = useState("type");
  const [visitType, setVisitType] = useState(null); // "cosmetic" | "health"
  const [photos, setPhotos] = useState({}); // { front: {url, uploadedAt}, left_45: {...}, ... }
  const [questionnaireAnswers, setQuestionnaireAnswers] = useState([]);
  // ★ Tracks whether we've finished checking the DB for an existing
  //   check-in row. Until this flips true, persistProgress is a no-op
  //   so we never blow away previously-saved photos with an empty state.
  //   This is the core fix for Frances's "photos disappeared after I
  //   re-did the check-in for a newly-assigned consent" bug.
  const [existingLoaded, setExistingLoaded] = useState(false);

  // ★ Load any prior check-in row on mount and seed local state with what
  //   it already has. This means: if staff opens the check-in AGAIN later
  //   (e.g. to sign a consent that was assigned after the initial check-in),
  //   the photos and questionnaire that were captured the first time are
  //   STILL visible and STILL saved. Without this, the second pass starts
  //   blank and the final write overwrites the row with empty arrays.
  useEffect(() => {
    if (!appointment?.id) { setExistingLoaded(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from("checkins")
          .select("face_photos,visit_type,questionnaire_answers")
          .eq("appointment_id", appointment.id)
          .maybeSingle();
        if (cancelled) return;
        if (data) {
          // Seed photos ONLY if our local state is still empty — never
          // clobber photos the user has already started capturing in this
          // session (e.g. if the DB read races with a fast camera grab).
          const existingPhotos = Array.isArray(data.face_photos) ? data.face_photos : [];
          if (existingPhotos.length > 0) {
            const m = {};
            for (const p of existingPhotos) {
              if (p?.angle && p?.url) m[p.angle] = { url: p.url, uploadedAt: p.uploaded_at };
            }
            setPhotos(prev => Object.keys(prev).length === 0 ? m : prev);
          }
          if (data.visit_type) {
            setVisitType(prev => prev || data.visit_type);
          }
          if (Array.isArray(data.questionnaire_answers) && data.questionnaire_answers.length) {
            setQuestionnaireAnswers(prev => prev.length === 0 ? data.questionnaire_answers : prev);
          }
        }
      } catch (e) {
        console.warn("checkin existing-row load failed:", e);
      } finally {
        if (!cancelled) setExistingLoaded(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointment?.id]);

  const pickType = (type) => {
    setVisitType(type);
    // Cosmetic visits go: type → photos → questionnaire → consents
    // Health visits go:   type → questionnaire → consents
    setStep(type === "cosmetic" ? "photos" : "questionnaire");
  };

  // Persist whatever the user has captured so far, so if they bail out
  // before finishing consents the photos aren't lost. Idempotent: each
  // call upserts the row keyed by appointment_id.
  const persistProgress = async (extras = {}) => {
    if (!appointment?.id) return;
    // ★ Guard: never write before we've checked for an existing row. The
    //   useEffect above seeds state from any prior check-in; if we write
    //   before that finishes, we could nuke the prior photos with [].
    if (!existingLoaded) return;
    try {
      const photoArr = Object.entries(photos).map(([angle, p]) => ({ angle, url: p.url, uploaded_at: p.uploadedAt }));
      const { data: existing } = await supabase
        .from("checkins")
        .select("id")
        .eq("appointment_id", appointment.id)
        .limit(1);
      const payload = {
        appointment_id: appointment.id,
        client_id: appointment.cid,
        client_name: appointment.cn,
        visit_type: visitType,
        questionnaire_answers: questionnaireAnswers,
        ...extras,
      };
      // ★ CRITICAL — only write face_photos on UPDATE when we actually
      //   have some. If state is empty, omit the field so an existing
      //   row's photos survive. (For INSERT we still write [] since the
      //   column may be NOT NULL on fresh inserts.)
      if (existing?.length) {
        if (photoArr.length > 0) payload.face_photos = photoArr;
        await supabase.from("checkins").update(payload).eq("id", existing[0].id);
      } else {
        payload.face_photos = photoArr;
        await supabase.from("checkins").insert([payload]);
      }
    } catch (e) {
      console.warn("checkin progressive save failed:", e);
    }
  };

  const onPhotosDone = async () => {
    // Save the photos as soon as the user finishes the photos step — that
    // way an interrupted flow (e.g. consents not signed) doesn't strand
    // uploaded photos with no DB row pointing at them.
    await persistProgress();
    setStep("questionnaire");
  };

  // CRITICAL: save immediately every time the photos object changes (i.e.
  // every successful upload or retake). Without this, photos uploaded to
  // storage but never reaching the "Continue" click — because the user
  // closed the modal — are stranded: file exists in the bucket, no DB row
  // points to it, and they vanish from the client's profile.
  // We compare a URL-based signature so a retake of the same angle also
  // triggers a save (count alone would miss it).
  const lastSavedSigRef = useRef("");
  useEffect(() => {
    const keys = Object.keys(photos);
    if (keys.length === 0) { lastSavedSigRef.current = ""; return; }
    const sig = keys.sort().map(k => `${k}:${photos[k]?.url || ""}`).join("|");
    if (sig === lastSavedSigRef.current) return;
    lastSavedSigRef.current = sig;
    persistProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos]);
  const onQuestionnaireDone = async (answers) => {
    setQuestionnaireAnswers(answers || []);
    await persistProgress({ questionnaire_answers: answers || [] });
    setStep("consents");
  };

  const onConsentsDone = (signedDocs) => {
    // Hand the full check-in payload back to the parent. The parent's
    // completeCheckIn handler ALSO upserts the row, so even if the early
    // saves above succeeded the final write is the authoritative version.
    onComplete({
      signedDocs,
      visit_type: visitType,
      face_photos: Object.entries(photos).map(([angle, p]) => ({ angle, url: p.url, uploaded_at: p.uploadedAt })),
      questionnaire_answers: questionnaireAnswers,
    });
  };

  if (step === "type") {
    return <VisitTypePicker onPick={pickType} onCancel={onCancel} client={client} appointment={appointment}/>;
  }
  if (step === "questionnaire") {
    return (
      <div>
        <div style={{padding:"10px 14px",background:visitType==="cosmetic"?C.goldBg:C.blueL,border:"1px solid "+(visitType==="cosmetic"?C.borderGold:C.blue+"40"),borderRadius:8,marginBottom:14,fontSize:12,color:visitType==="cosmetic"?C.gold:C.blue,fontWeight:600,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:6}}>
          <span>{visitType === "cosmetic" ? `✨ Cosmetic · ${Object.keys(photos).length}/5 photos captured` : "💊 Health visit"}</span>
          <button onClick={()=>setStep(visitType === "cosmetic" ? "photos" : "type")} style={{background:"none",border:"none",color:"inherit",fontWeight:700,cursor:"pointer",textDecoration:"underline",fontSize:11}}>← Back</button>
        </div>
        <QuestionnaireRunner appointment={appointment} onComplete={onQuestionnaireDone} onCancel={()=>setStep(visitType === "cosmetic" ? "photos" : "type")}/>
      </div>
    );
  }
  if (step === "photos") {
    return <PhotoCaptureStep photos={photos} setPhotos={setPhotos} onDone={onPhotosDone} onBack={()=>setStep("type")} client={client} appointment={appointment}/>;
  }
  return (
    <div>
      <div style={{padding:"10px 14px",background:visitType==="cosmetic"?C.goldBg:C.blueL,border:"1px solid "+(visitType==="cosmetic"?C.borderGold:C.blue+"40"),borderRadius:8,marginBottom:14,fontSize:12,color:visitType==="cosmetic"?C.gold:C.blue,fontWeight:600}}>
        {visitType === "cosmetic" ? `✨ Cosmetic visit · ${Object.keys(photos).length}/5 face photos captured` : "💊 Health visit · No photos needed"}
        <button onClick={()=>setStep(visitType === "cosmetic" ? "photos" : "type")} style={{marginLeft:10,background:"none",border:"none",color:"inherit",fontWeight:700,cursor:"pointer",textDecoration:"underline"}}>change</button>
      </div>
      <ConsentSigningFlow client={client} appointment={appointment} onComplete={onConsentsDone} onCancel={onCancel}/>
    </div>
  );
}

function VisitTypePicker({ onPick, onCancel, client, appointment }) {
  return (
    <div>
      <div style={{textAlign:"center",marginBottom:24}}>
        <div style={{fontSize:13,color:C.muted,marginBottom:6}}>Check-in for</div>
        <div style={{fontSize:20,fontWeight:800,color:C.gold,fontFamily:S}}>{client?.name || appointment?.cn}</div>
        <div style={{fontSize:12,color:C.muted,marginTop:3}}>{appointment?.treat} with {appointment?.prov}</div>
      </div>
      <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:14,textAlign:"center"}}>What type of visit is this?</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
        <button onClick={()=>onPick("cosmetic")} style={{padding:"24px 18px",background:"#fff",border:"2px solid "+C.borderGold,borderRadius:14,cursor:"pointer",fontFamily:F,textAlign:"center",transition:"all 0.2s",boxShadow:"0 2px 8px rgba(180,140,60,0.08)"}} onMouseEnter={e=>{e.currentTarget.style.borderColor=C.gold;e.currentTarget.style.transform="translateY(-2px)";}} onMouseLeave={e=>{e.currentTarget.style.borderColor=C.borderGold;e.currentTarget.style.transform="translateY(0)";}}>
          <div style={{fontSize:36,marginBottom:8}}>✨</div>
          <div style={{fontSize:16,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:4}}>Cosmetic</div>
          <div style={{fontSize:11,color:C.muted,lineHeight:1.5}}>Botox, Filler, Laser, Facials, Skin Treatments</div>
          <div style={{fontSize:10,color:C.gold,fontWeight:600,marginTop:8,padding:"4px 8px",background:C.goldBg,borderRadius:6,display:"inline-block"}}>📸 5 photos required</div>
        </button>
        <button onClick={()=>onPick("health")} style={{padding:"24px 18px",background:"#fff",border:"2px solid "+C.blue+"40",borderRadius:14,cursor:"pointer",fontFamily:F,textAlign:"center",transition:"all 0.2s",boxShadow:"0 2px 8px rgba(37,99,235,0.08)"}} onMouseEnter={e=>{e.currentTarget.style.borderColor=C.blue;e.currentTarget.style.transform="translateY(-2px)";}} onMouseLeave={e=>{e.currentTarget.style.borderColor=C.blue+"40";e.currentTarget.style.transform="translateY(0)";}}>
          <div style={{fontSize:36,marginBottom:8}}>💊</div>
          <div style={{fontSize:16,fontWeight:800,color:C.blue,fontFamily:S,marginBottom:4}}>Health</div>
          <div style={{fontSize:11,color:C.muted,lineHeight:1.5}}>IV Therapy, GLP-1, Wellness, Hormone</div>
          <div style={{fontSize:10,color:C.blue,fontWeight:600,marginTop:8,padding:"4px 8px",background:C.blueL,borderRadius:6,display:"inline-block"}}>📋 Consents only</div>
        </button>
      </div>
      <div style={{textAlign:"center",marginTop:20}}>
        <button onClick={onCancel} style={{background:"none",border:"none",color:C.muted,fontSize:12,cursor:"pointer",fontFamily:F,textDecoration:"underline"}}>Cancel check-in</button>
      </div>
    </div>
  );
}

function PhotoCaptureStep({ photos, setPhotos, onDone, onBack, client, appointment }) {
  const [active, setActive] = useState(PHOTO_ANGLES[0].key);
  const completedCount = Object.keys(photos).length;
  const activeAngle = PHOTO_ANGLES.find(a => a.key === active);
  const activePhoto = photos[active];

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
        <div>
          <div style={{fontSize:11,color:C.muted}}>Cosmetic check-in for {client?.name || appointment?.cn}</div>
          <div style={{fontSize:14,fontWeight:800,color:C.gold,fontFamily:S}}>📸 Face Photos ({completedCount} of 5)</div>
        </div>
        <button onClick={onBack} style={{padding:"6px 12px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:7,fontSize:11,cursor:"pointer",fontFamily:F}}>← Change visit type</button>
      </div>

      {/* Angle tabs — auto-fit wraps to 2 rows on phone instead of cramming
          5 squares into a 390px-wide screen. minmax(64px) keeps each tile
          tappable. */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(64px, 1fr))",gap:6,marginBottom:16}}>
        {PHOTO_ANGLES.map(a => {
          const done = !!photos[a.key];
          const isActive = a.key === active;
          return (
            <button key={a.key} onClick={()=>setActive(a.key)} style={{minHeight:56,padding:"10px 4px",background:isActive?C.goldBg:done?C.greenL:C.light,border:"2px solid "+(isActive?C.gold:done?C.green:C.border),borderRadius:10,cursor:"pointer",fontFamily:F,textAlign:"center",touchAction:"manipulation"}}>
              <div style={{fontSize:22,marginBottom:2}}>{done?"✓":a.emoji}</div>
              <div style={{fontSize:11,fontWeight:700,color:isActive?C.gold:done?C.green:C.muted}}>{a.label}</div>
            </button>
          );
        })}
      </div>

      {/* Active angle capture */}
      <PhotoCaptureCell
        angle={activeAngle}
        photo={activePhoto}
        clientId={client?.id}
        appointmentId={appointment?.id}
        onUploaded={(p)=>setPhotos({...photos, [active]: p})}
        onCleared={()=>{const np={...photos}; delete np[active]; setPhotos(np);}}
      />

      <div style={{display:"flex",gap:8,justifyContent:"space-between",marginTop:20,flexWrap:"wrap"}}>
        <div style={{display:"flex",gap:6}}>
          {PHOTO_ANGLES.map((a,i)=>{
            if(a.key!==active)return null;
            const prev=PHOTO_ANGLES[i-1];
            const next=PHOTO_ANGLES[i+1];
            return (
              <span key="nav" style={{display:"contents"}}>
                {prev && <button onClick={()=>setActive(prev.key)} style={{padding:"9px 14px",background:C.light,color:C.text,border:"1px solid "+C.border,borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:F}}>← {prev.label}</button>}
                {next && <button onClick={()=>setActive(next.key)} disabled={!activePhoto} style={{padding:"9px 14px",background:activePhoto?C.goldBg:C.light,color:activePhoto?C.gold:C.dim,border:"1px solid "+(activePhoto?C.borderGold:C.border),borderRadius:8,fontSize:12,cursor:activePhoto?"pointer":"not-allowed",fontFamily:F,fontWeight:600}}>{next.label} →</button>}
              </span>
            );
          })}
        </div>
        {/* Two parallel buttons: Skip = move on without all 5 photos
            (photos can be uploaded later from the client's profile),
            Continue = standard happy path with all 5 captured. */}
        <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
          <button
            onClick={() => {
              if (completedCount === 0) {
                if (!window.confirm("Skip photos for now? You can upload them later from this client's profile.")) return;
              }
              onDone();
            }}
            style={{padding:"10px 18px",background:"#fff",color:C.muted,border:"1px solid "+C.border,borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:F}}
          >
            ⏭ Skip photos for now
          </button>
          <button
            onClick={onDone}
            disabled={completedCount < 5}
            style={{padding:"10px 22px",background:completedCount<5?C.light:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:completedCount<5?C.muted:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:completedCount<5?"not-allowed":"pointer",fontFamily:F,marginLeft:"auto"}}
          >
            {completedCount < 5 ? `${completedCount}/5 captured — fill all 5 to use Continue` : "Continue to Consents →"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PhotoCaptureCell({ angle, photo, clientId, appointmentId, onUploaded, onCleared }) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { setErr("Please pick an image file"); return; }
    if (file.size > 10 * 1024 * 1024) { setErr("Photo must be under 10MB"); return; }
    // Refuse to upload if we don't know which client this is for — otherwise
    // photos land under "client-unknown/" in storage and nobody can find them
    // later. Better to surface the bug than to silently lose data.
    if (!clientId) {
      setErr("Cannot save photo — client info is missing. Close and reopen the check-in.");
      return;
    }
    setErr("");
    setUploading(true);
    try {
      const ext = (file.name?.split(".").pop() || "jpg").toLowerCase();
      const path = `client-${clientId}/appt-${appointmentId || "no-appt"}/${angle.key}-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("checkin-photos").upload(path, file, { cacheControl: "3600", upsert: true });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from("checkin-photos").getPublicUrl(path);
      onUploaded({ url: pub.publicUrl, uploadedAt: new Date().toISOString() });
    } catch (e) {
      setErr(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,padding:18}}>
      <div style={{textAlign:"center",marginBottom:14}}>
        <div style={{fontSize:32,marginBottom:6}}>{angle.emoji}</div>
        <div style={{fontSize:15,fontWeight:800,color:C.gold,fontFamily:S}}>{angle.label}</div>
        <div style={{fontSize:11,color:C.muted,marginTop:4,maxWidth:380,margin:"4px auto 0"}}>{angle.instruction}</div>
      </div>

      {photo?.url ? (
        <div style={{position:"relative",textAlign:"center"}}>
          <img src={photo.url} alt={angle.label} style={{maxWidth:"100%",maxHeight:360,borderRadius:10,boxShadow:"0 4px 14px rgba(0,0,0,0.12)"}}/>
          <div style={{marginTop:10,display:"flex",gap:8,justifyContent:"center"}}>
            <button onClick={()=>cameraRef.current?.click()} disabled={uploading} style={{padding:"8px 14px",background:C.goldBg,color:C.gold,border:"1px solid "+C.borderGold,borderRadius:7,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>📸 Retake</button>
            <button onClick={onCleared} style={{padding:"8px 14px",background:C.redL,color:C.red,border:"1px solid "+C.red+"40",borderRadius:7,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>🗑 Remove</button>
          </div>
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:10,padding:"30px 20px",background:C.light,borderRadius:10,border:"2px dashed "+C.border}}>
          <div style={{fontSize:42,opacity:0.4}}>📷</div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap",justifyContent:"center"}}>
            <button onClick={()=>cameraRef.current?.click()} disabled={uploading} style={{padding:"10px 18px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:uploading?"not-allowed":"pointer",fontFamily:F}}>
              {uploading ? "Uploading..." : "📸 Take photo"}
            </button>
            <button onClick={()=>fileRef.current?.click()} disabled={uploading} style={{padding:"10px 18px",background:"#fff",color:C.text,border:"1px solid "+C.border,borderRadius:8,fontSize:12,fontWeight:700,cursor:uploading?"not-allowed":"pointer",fontFamily:F}}>
              📁 Upload from device
            </button>
          </div>
          <div style={{fontSize:10,color:C.muted,textAlign:"center",maxWidth:300,lineHeight:1.5}}>
            JPG or PNG, up to 10MB. On a tablet/phone "Take photo" opens the camera directly.
          </div>
        </div>
      )}

      <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={(e)=>handleFile(e.target.files?.[0])} style={{display:"none"}}/>
      <input ref={fileRef} type="file" accept="image/*" onChange={(e)=>handleFile(e.target.files?.[0])} style={{display:"none"}}/>

      {err && <div style={{marginTop:8,padding:"8px 12px",background:C.redL,color:C.red,borderRadius:7,fontSize:11,fontWeight:600}}>{err}</div>}
    </div>
  );
}
